import type { Theme, ThemeRegistry } from './types';

/**
 * Module-level list of registered themes. Populated by `registerTheme()` at
 * import time. Side-effect registration is intentional: theme plugins self-
 * wire without a manual central list, and HMR's repeated re-imports are
 * idempotent because `registerTheme` short-circuits on duplicate slugs.
 */
const themes: Theme[] = [];

/**
 * Register a theme plugin. Idempotent by `slug` — re-registration with the
 * same slug is a no-op (the first registration wins).
 */
export function registerTheme(theme: Theme): void {
  if (themes.some((t) => t.slug === theme.slug)) {
    return;
  }
  themes.push(theme);
}

export const themeRegistry: ThemeRegistry = {
  list: () => themes,
  get: (slug) => themes.find((t) => t.slug === slug),
  defaultTheme: () => {
    if (themes.length === 0) {
      throw new Error(
        '[themes] No themes registered. Import a theme module before calling themeRegistry.defaultTheme().'
      );
    }
    // Non-null assertion guarded by the empty check above.
    return themes[0]!;
  },
};

/** Test-only escape hatch: clear the registry between tests. Not exported in barrel. */
export function __resetThemesForTests(): void {
  themes.length = 0;
}